import { ReactElement } from 'react';
import clsx from 'clsx';

export type DashboardView = 'simple' | 'detailed' | 'map';

type ViewOption = {
  id: DashboardView;
  label: string;
  icon: string;
};

const VIEW_OPTIONS: ViewOption[] = [
  { id: 'simple', label: 'Overview', icon: 'grid_view' },
  { id: 'detailed', label: 'Detailed', icon: 'dashboard' },
  { id: 'map', label: 'Map', icon: 'map' }
];

type ViewModeToggleProps = {
  view: DashboardView;
  onChange: (view: DashboardView) => void;
  /** Views to offer; anything omitted is hidden (feature-gated). */
  availableViews?: DashboardView[];
};

/**
 * A professional segmented control to switch between the Overview, Detailed and Map dashboard views.
 * @param props - The props for the ViewModeToggle component.
 * @returns A React element representing the segmented view switcher.
 */
export default function ViewModeToggle(props: ViewModeToggleProps): ReactElement {
  const { view, onChange, availableViews } = props;
  const options = availableViews
    ? VIEW_OPTIONS.filter((option) => availableViews.includes(option.id))
    : VIEW_OPTIONS;

  return (
    <div
      role="tablist"
      aria-label="Dashboard view"
      className="inline-flex items-center gap-0.5 rounded-lg border border-gray-300 bg-gray-100 p-0.5 shadow-xs dark:border-background-700 dark:bg-background-800"
    >
      {options.map((option) => {
        const isActive = view === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            title={`${option.label} view`}
            onClick={() => onChange(option.id)}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              {
                'bg-white text-primary-600 shadow-sm dark:bg-background-600 dark:text-primary-300':
                  isActive,
                'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200':
                  !isActive
              }
            )}
          >
            <span className="material-icons-outlined text-lg leading-none">{option.icon}</span>
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
